import { GS1_AI_TABLE } from './gs1-ai-table.generated.js';
import { runLinter } from './linters.js';
import { aiMatchesPattern } from './syntax-dictionary.js';
const TABLE = new Map(GS1_AI_TABLE.map((entry) => [entry.ai, entry]));
/** « Convenience alphas » retirés depuis URI Syntax 1.3.0 : reconnus pour un motif d'erreur clair. */
const CONVENIENCE_ALPHAS = new Set(['gtin', 'itip', 'gmn', 'cpid', 'gln', 'party', 'gsrnp', 'gsrn', 'gcn', 'sscc', 'gdti', 'ginc', 'gsin', 'grai', 'giai', 'cpv', 'lot', 'ser', 'glnx']);
const DIGITS = /^\d+$/;
const BASE64URL = /^[A-Za-z0-9_-]+$/;
const HTTP_URI = /^https?:\/\/([^/?#]+)([^?#]*)(?:\?([^#]*))?(?:#.*)?$/i;
class ParseFailure extends Error {
    detail;
    constructor(detail) {
        super(detail.message);
        this.detail = detail;
    }
}
const failure = (code, message, ai) => new ParseFailure(ai === undefined ? { code, message } : { code, message, ai });
/** Entrée du dictionnaire d'un AI. */
export function aiEntry(ai) {
    return TABLE.get(ai);
}
/** AI marqués `dlpkey` dans le dictionnaire. */
export function digitalLinkPrimaryKeys() {
    return GS1_AI_TABLE.filter((entry) => entry.dlpkey !== undefined).map((entry) => entry.ai);
}
/**
 * Décodage pour-cent octet par octet, comme le Syntax Engine : `%XX` valide est décodé, un `%`
 * sans deux chiffres hexadécimaux reste littéral (il est alors jugé par le jeu de caractères). Un
 * `+` reste un `+` : ce n'est pas un espace dans une URI Digital Link.
 */
function percentDecode(text) {
    let out = '';
    for (let index = 0; index < text.length; index += 1) {
        const char = text[index];
        if (char === '%' && index + 2 < text.length && /^[0-9A-Fa-f]{2}$/.test(text.slice(index + 1, index + 3))) {
            out += String.fromCharCode(parseInt(text.slice(index + 1, index + 3), 16));
            index += 2;
        }
        else {
            out += char;
        }
    }
    return out;
}
const CSET82 = /^[!"%&'()*+,\-./0-9:;<=>?A-Z_a-z]*$/;
const CSET39 = /^[#\-/0-9A-Z]*$/;
const CSET64 = /^[0-9A-Za-z_-]*$/;
function charsetProblem(component, data) {
    switch (component.charset) {
        case 'N':
            return DIGITS.test(data) ? null : 'un caractère non numérique est présent';
        case 'X':
            return CSET82.test(data) ? null : 'un caractère hors CSET 82 est présent';
        case 'Y':
            return CSET39.test(data) ? null : 'un caractère hors CSET 39 est présent';
        case 'Z': {
            let pads = 0;
            while (pads < data.length && data[data.length - 1 - pads] === '=')
                pads += 1;
            if (pads > 2 || (pads > 0 && data.length % 3 !== 0))
                return 'remplissage base64url invalide';
            return CSET64.test(data.slice(0, data.length - pads)) ? null : 'un caractère hors CSET 64 est présent';
        }
    }
}
/** Valide la valeur d'un AI contre ses composants et linters ; lève une `ParseFailure`. */
function validateValue(entry, value) {
    let rest = value;
    for (const [index, component] of entry.components.entries()) {
        if (rest.length === 0) {
            if (component.optional)
                return;
            throw failure('GS1_VALUE_INVALID', `AI (${entry.ai}) : la valeur est trop courte.`, entry.ai);
        }
        const last = index === entry.components.length - 1;
        let part;
        if (component.min === component.max) {
            if (rest.length < component.max)
                throw failure('GS1_VALUE_INVALID', `AI (${entry.ai}) : la valeur est trop courte.`, entry.ai);
            part = rest.slice(0, component.max);
        }
        else {
            if (!last)
                throw failure('GS1_VALUE_INVALID', `AI (${entry.ai}) : spécification du dictionnaire non prise en charge.`, entry.ai);
            if (rest.length > component.max)
                throw failure('GS1_VALUE_INVALID', `AI (${entry.ai}) : la valeur est trop longue.`, entry.ai);
            if (rest.length < component.min)
                throw failure('GS1_VALUE_INVALID', `AI (${entry.ai}) : la valeur est trop courte.`, entry.ai);
            part = rest;
        }
        rest = rest.slice(part.length);
        const charset = charsetProblem(component, part);
        if (charset !== null)
            throw failure('GS1_VALUE_INVALID', `AI (${entry.ai}) : ${charset}.`, entry.ai);
        for (const linter of component.linters) {
            const problem = runLinter(linter, part);
            if (problem !== null)
                throw failure(linter === 'csum' || linter === 'csumalpha' ? 'GS1_CHECK_DIGIT_INVALID' : 'GS1_VALUE_INVALID', `AI (${entry.ai}) : ${problem}.`, entry.ai);
        }
    }
    if (rest.length > 0)
        throw failure('GS1_VALUE_INVALID', `AI (${entry.ai}) : la valeur est trop longue.`, entry.ai);
}
/** Règles `req` et `ex` du dictionnaire, évaluées sur l'ensemble des AI de la donnée. */
function validateAssociations(elements) {
    const ais = elements.map((element) => element.ai);
    for (const ai of ais) {
        const entry = TABLE.get(ai);
        for (const pattern of entry.ex) {
            const clash = ais.find((other) => other !== ai && aiMatchesPattern(other, pattern));
            if (clash !== undefined)
                throw failure('GS1_AI_ASSOCIATION', `L'AI (${clash}) ne peut pas accompagner l'AI (${ai}).`, ai);
        }
        for (const rule of entry.req) {
            const satisfied = rule.some((alternative) => alternative.every((pattern) => ais.some((other) => aiMatchesPattern(other, pattern))));
            if (!satisfied)
                throw failure('GS1_AI_ASSOCIATION', `L'AI (${ai}) exige ${rule.map((alternative) => alternative.map((pattern) => `(${pattern})`).join(' + ')).join(' ou ')}.`, ai);
        }
    }
}
function assertNoDuplicate(elements) {
    const seen = new Set();
    for (const element of elements) {
        if (seen.has(element.ai))
            throw failure('GS1_URI_INVALID', `L'AI (${element.ai}) apparaît deux fois.`, element.ai);
        seen.add(element.ai);
    }
}
/** Vrai si les qualifiants suivent, dans l'ordre, l'une des séquences `dlpkey` de la clé. */
function qualifierSequenceValid(alternatives, qualifiers) {
    return alternatives.some((sequence) => {
        let cursor = 0;
        for (const ai of qualifiers) {
            const found = sequence.indexOf(ai, cursor);
            if (found === -1)
                return false;
            cursor = found + 1;
        }
        return true;
    });
}
const segmentsOf = (path) => path.slice(1).split('/');
/**
 * Forme compressée (URI Syntax 1.7.0 §4.12) : aucune paire « AI connu / valeur » dans le chemin,
 * et un dernier segment en base64url d'au moins huit caractères.
 */
export function isCompressedDigitalLinkPath(path) {
    if (!path.startsWith('/'))
        return false;
    const trimmed = path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
    const segments = segmentsOf(trimmed);
    const last = segments[segments.length - 1] ?? '';
    if (last.length < 8 || !BASE64URL.test(last) || DIGITS.test(last))
        return false;
    for (let index = 0; index + 1 < segments.length; index += 1) {
        if (DIGITS.test(segments[index]) && TABLE.has(segments[index]))
            return false;
    }
    return true;
}
function parseQuery(query, primary, qualifierAis) {
    const attributes = [];
    const parameters = [];
    for (const piece of query.split('&')) {
        if (piece === '')
            continue;
        const equals = piece.indexOf('=');
        if (equals === -1) {
            parameters.push([percentDecode(piece), '']);
            continue;
        }
        const key = piece.slice(0, equals);
        const value = percentDecode(piece.slice(equals + 1));
        if (!DIGITS.test(key)) {
            parameters.push([percentDecode(key), value]);
            continue;
        }
        const entry = TABLE.get(key);
        if (entry === undefined)
            throw failure('GS1_AI_UNKNOWN', `AI (${key}) inconnu dans la chaîne de requête.`, key);
        if (qualifierAis.has(key))
            throw failure('GS1_AI_NOT_PERMITTED', `L'AI (${key}) est un qualifiant de la clé (${primary.ai}) : il figure dans le chemin, pas dans la requête.`, key);
        if (!entry.dl_attribute)
            throw failure('GS1_AI_NOT_PERMITTED', `L'AI (${key}) n'est pas un attribut de données admis dans une URI Digital Link.`, key);
        validateValue(entry, value);
        attributes.push({ ai: key, value });
    }
    return { attributes, parameters };
}
function parsePath(rawPath, query, options) {
    if (!rawPath.startsWith('/'))
        throw failure('GS1_URI_INVALID', 'Le chemin d’une URI Digital Link commence par « / ».');
    const trailing_slash = rawPath.length > 1 && rawPath.endsWith('/');
    const path = trailing_slash ? rawPath.slice(0, -1) : rawPath;
    const segments = segmentsOf(path);
    let keyIndex;
    for (let index = segments.length; index >= 2; index -= 2) {
        const candidate = segments[index - 2];
        if (!DIGITS.test(candidate) || !TABLE.has(candidate))
            break;
        if (TABLE.get(candidate).dlpkey !== undefined) {
            keyIndex = index - 2;
            break;
        }
    }
    if (keyIndex === undefined) {
        if (isCompressedDigitalLinkPath(rawPath))
            throw failure('GS1_URI_COMPRESSED', 'Les URI Digital Link compressées ne sont pas prises en charge (phase 5) : utilisez la forme non compressée.');
        const alpha = segments.find((segment) => CONVENIENCE_ALPHAS.has(segment.toLowerCase()));
        if (alpha !== undefined)
            throw failure('GS1_URI_INVALID', `« ${alpha} » est un « convenience alpha », retiré de la syntaxe Digital Link depuis la version 1.3.0 : utilisez le numéro d'AI.`);
        throw failure('GS1_URI_INVALID', 'Aucune clé primaire GS1 Digital Link n’a été trouvée dans le chemin (segments par paires AI / valeur).');
    }
    const pairs = [];
    for (let index = keyIndex; index < segments.length; index += 2) {
        const value = percentDecode(segments[index + 1]);
        if (value === '')
            throw failure('GS1_URI_INVALID', `AI (${segments[index]}) : la valeur du segment de chemin est vide.`, segments[index]);
        pairs.push({ ai: segments[index], value });
    }
    const primary = pairs[0];
    const entry = TABLE.get(primary.ai);
    if (options.primaryKeys !== undefined && !options.primaryKeys.includes(primary.ai)) {
        throw failure('GS1_PRIMARY_KEY_UNSUPPORTED', `La clé primaire (${primary.ai}) n'est pas prise en charge par ce résolveur.`, primary.ai);
    }
    const qualifiers = pairs.slice(1);
    if (!qualifierSequenceValid(entry.dlpkey, qualifiers.map((qualifier) => qualifier.ai))) {
        throw failure('GS1_QUALIFIER_ORDER', `Les AI du chemin ne forment pas une séquence de qualifiants valide pour la clé (${primary.ai}).`, primary.ai);
    }
    for (const element of pairs)
        validateValue(TABLE.get(element.ai), element.value);
    const qualifierAis = new Set(entry.dlpkey.flat());
    const { attributes, parameters } = parseQuery(query, primary, qualifierAis);
    const elements = [...pairs, ...attributes];
    assertNoDuplicate(elements);
    validateAssociations(elements);
    const parsed = { primary, qualifiers, attributes, parameters, trailing_slash };
    const linkType = parameters.find(([key]) => key === 'linkType');
    if (linkType !== undefined)
        parsed.link_type = linkType[1];
    return parsed;
}
const capture = (run) => {
    try {
        return { ok: true, value: run() };
    }
    catch (error) {
        if (error instanceof ParseFailure)
            return { ok: false, error: error.detail };
        throw error;
    }
};
/** Analyse le chemin (et la chaîne de requête, sans `?`) d'une URI Digital Link. */
export function parseDigitalLinkPath(path, query = '', options = {}) {
    return capture(() => parsePath(path, query.startsWith('?') ? query.slice(1) : query, options));
}
/** Analyse une URI Digital Link complète, http ou https ; un préfixe de chemin (stem) est admis. */
export function parseDigitalLinkUri(uri, options = {}) {
    const match = HTTP_URI.exec(uri.trim());
    if (match === null)
        return { ok: false, error: { code: 'GS1_URI_INVALID', message: 'Une URI Digital Link est une URI http ou https.' } };
    return capture(() => parsePath(match[2] === '' ? '/' : match[2], match[3] ?? '', options));
}
/**
 * Analyse une chaîne d'éléments GS1 : forme parenthésée `(01)…(10)…` (une parenthèse dans une
 * valeur s'écrit `\(`), ou forme brute séparée par GS (U+001D) ou `^`, avec ou sans identifiant de
 * symbologie (`]d2`, `]Q3`, `]C1`, `]e0`).
 */
export function parseElementString(input) {
    try {
        let data = input;
        const symbology = /^\](d2|Q3|C1|e0)/.exec(data);
        if (symbology !== null)
            data = data.slice(3);
        const elements = data.startsWith('(') ? bracketed(data) : unbracketed(data);
        if (elements.length === 0)
            throw failure('GS1_URI_INVALID', 'La chaîne d’éléments est vide.');
        for (const element of elements)
            validateValue(TABLE.get(element.ai), element.value);
        assertNoDuplicate(elements);
        validateAssociations(elements);
        return { ok: true, elements };
    }
    catch (error) {
        if (error instanceof ParseFailure)
            return { ok: false, error: error.detail };
        throw error;
    }
}
function knownAi(ai) {
    const entry = TABLE.get(ai);
    if (entry === undefined)
        throw failure('GS1_AI_UNKNOWN', `AI (${ai}) inconnu.`, ai);
    return entry;
}
function bracketed(data) {
    const elements = [];
    let index = 0;
    while (index < data.length) {
        if (data[index] !== '(')
            throw failure('GS1_URI_INVALID', 'Chaîne d’éléments parenthésée mal formée.');
        const close = data.indexOf(')', index);
        const ai = close === -1 ? '' : data.slice(index + 1, close);
        if (!DIGITS.test(ai))
            throw failure('GS1_URI_INVALID', 'Chaîne d’éléments parenthésée mal formée.');
        knownAi(ai);
        index = close + 1;
        let value = '';
        while (index < data.length && data[index] !== '(') {
            if (data[index] === '\\' && data[index + 1] === '(') {
                value += '(';
                index += 2;
            }
            else {
                value += data[index];
                index += 1;
            }
        }
        elements.push({ ai, value });
    }
    return elements;
}
function unbracketed(data) {
    const elements = [];
    let index = data.startsWith('^') ? 1 : 0;
    const separator = (char) => char === '\u001d' || char === '^';
    while (index < data.length) {
        let entry;
        for (const length of [2, 3, 4]) {
            entry = TABLE.get(data.slice(index, index + length));
            if (entry !== undefined)
                break;
        }
        if (entry === undefined)
            throw failure('GS1_AI_UNKNOWN', `AI inconnu à la position ${index + 1} de la chaîne d’éléments.`);
        index += entry.ai.length;
        let value;
        if (entry.fixed_length) {
            const length = entry.components.reduce((total, component) => total + component.max, 0);
            value = data.slice(index, index + length);
            index += value.length;
        }
        else {
            let end = index;
            while (end < data.length && !separator(data[end]))
                end += 1;
            value = data.slice(index, end);
            index = end;
        }
        elements.push({ ai: entry.ai, value });
        if (separator(data[index]))
            index += 1;
    }
    return elements;
}
/** Encodage pour-cent RFC 3986 : seuls les caractères non réservés restent littéraux (`/` → `%2F`). */
function encodeSegment(value) {
    let out = '';
    for (const byte of new TextEncoder().encode(value)) {
        const char = String.fromCharCode(byte);
        out += /^[A-Za-z0-9\-._~]$/.test(char) ? char : `%${byte.toString(16).toUpperCase().padStart(2, '0')}`;
    }
    return out;
}
/** Chemin Digital Link d'une clé et de ses qualifiants : `/01/{gtin}/10/{lot}`. */
export function buildDigitalLinkPath(primary, qualifiers) {
    return [primary, ...qualifiers].map((element) => `/${element.ai}/${encodeSegment(element.value)}`).join('');
}
//# sourceMappingURL=parse.js.map